import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export type SmartBlurMode = "normal" | "edge-only" | "overlay";

export interface SmartBlurParams {
  radius: number;
  threshold: number;
  quality: number;
  mode: SmartBlurMode;
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

export const SmartBlur: Filter<SmartBlurParams> = {
  id: "smart-blur",
  displayName: "Smart Blur",
  defaultParams: { radius: 5, threshold: 25, quality: 3, mode: "normal" },

  apply(input: PixelBuffer, params: SmartBlurParams, _selection: Selection | null): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0 || params.radius < 1) {
      dst.set(src);
      return out;
    }

    const r = Math.round(params.radius);
    const threshold = params.threshold;
    const quality = Math.max(1, Math.round(params.quality));

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const o = (y * w + x) * 4;
        const cR = src[o]!, cG = src[o + 1]!, cB = src[o + 2]!;
        let sumR = 0, sumG = 0, sumB = 0, sumA = 0, weightTotal = 0;

        for (let dy = -r; dy <= r; dy += quality) {
          for (let dx = -r; dx <= r; dx += quality) {
            const sx = Math.max(0, Math.min(w - 1, x + dx));
            const sy = Math.max(0, Math.min(h - 1, y + dy));
            const si = (sy * w + sx) * 4;
            const diff = Math.abs(src[si]! - cR) + Math.abs(src[si + 1]! - cG) + Math.abs(src[si + 2]! - cB);
            const weight = diff <= threshold ? 1 : 0;
            sumR += src[si]! * weight;
            sumG += src[si + 1]! * weight;
            sumB += src[si + 2]! * weight;
            sumA += src[si + 3]! * weight;
            weightTotal += weight;
          }
        }

        if (weightTotal > 0) {
          const bR = Math.round(sumR / weightTotal);
          const bG = Math.round(sumG / weightTotal);
          const bB = Math.round(sumB / weightTotal);

          if (params.mode === "edge-only") {
            const edge = Math.abs(cR - bR) + Math.abs(cG - bG) + Math.abs(cB - bB);
            dst[o] = clamp8(edge);
            dst[o + 1] = clamp8(edge);
            dst[o + 2] = clamp8(edge);
            dst[o + 3] = src[o + 3]!;
          } else if (params.mode === "overlay") {
            dst[o] = clamp8((cR + bR) / 2);
            dst[o + 1] = clamp8((cG + bG) / 2);
            dst[o + 2] = clamp8((cB + bB) / 2);
            dst[o + 3] = src[o + 3]!;
          } else {
            dst[o] = bR; dst[o + 1] = bG; dst[o + 2] = bB; dst[o + 3] = src[o + 3]!;
          }
        } else {
          dst[o] = cR; dst[o + 1] = cG; dst[o + 2] = cB; dst[o + 3] = src[o + 3]!;
        }
      }
    }
    return out;
  },
};
